import { useCallback, useMemo, useState, type ReactNode } from "react"
import { GetApp } from "../../../App"
import { useDataStore } from "../../../Data/Context"
import { isShipUser, mergeLiveMembership } from "../../../Data/ShipUserMembership"
import { useRetainedEntities } from "../../../Data/useRetainedEntities"
import { required, useExtras, useQuery } from "../../../Data/useStore"
import { AllShipsContext, type AllShipsData } from "./Context"

export function AllShipsDataView({ children }: { children?: ReactNode }) {
  const app = GetApp();
  const store = useDataStore();
  const userId = app.user?.id;
  const storedShips = useQuery(store, "Ship", {});
  const storedMembership = useQuery(store, "ShipUser", { userId: required(userId) });
  const extras = useExtras(store, "ShipUser", { userId });
  const [loaded, setLoaded] = useState(false);

  useRetainedEntities(["Ship", "ShipUser"]);

  const load = useCallback(async (force?: boolean) => {
    if (loaded && force != true) {
      return;
    }
    if (userId == undefined) {
      return;
    }
    await store.fetchQueryStrict("ShipUser", { userId });
    setLoaded(true);
  }, [loaded, store, userId]);

  const ships = useMemo(
    () => loaded ? [...storedShips] : undefined,
    [loaded, storedShips],
  );
  const membership = useMemo(() => {
    if (!loaded || !Array.isArray(extras.membership)) {
      return undefined;
    }
    return mergeLiveMembership(extras.membership.filter(isShipUser), storedMembership);
  }, [extras.membership, loaded, storedMembership]);
  const ownShipId = loaded && typeof extras.ownShipId == "number"
    ? extras.ownShipId
    : undefined;
  const value: AllShipsData = { ships, membership, ownShipId, load };

  return <AllShipsContext.Provider value={value}>{children}</AllShipsContext.Provider>;
}
